/**
 * UC-23: EvaluateEnhancedObjectives
 * TOGAF Training context ABB — secondary objective evaluation without UI deps.
 */
import type { CityStats } from '../../../types';
import type {
  EnhancedObjective,
  ObjectiveContext,
  ObjectiveNotification,
} from '../../../types/enhancedObjectives';
import type { IEnhancedObjectivesEvaluator } from '../../domain/ports/IEnhancedObjectivesEvaluator';
import type { IAppLogger } from '../../domain/ports/IAppLogger';

export interface EvaluateEnhancedObjectivesInput {
  stats: CityStats;
  buildingCounts: Record<string, number>;
  researchCompletedTechs: string[];
  gameSpeed: number;
  scenarioId: string | null;
}

export interface EvaluateEnhancedObjectivesOutput {
  notifications: ObjectiveNotification[];
}

export interface EvaluateEnhancedObjectivesDependencies {
  evaluator: IEnhancedObjectivesEvaluator;
  logger?: IAppLogger;
}

export class EvaluateEnhancedObjectivesUseCase {
  constructor(private readonly deps: EvaluateEnhancedObjectivesDependencies) {}

  registerObjectives(objectives: EnhancedObjective[]): void {
    for (const objective of objectives) {
      this.deps.evaluator.addObjective(objective);
    }

    this.deps.logger?.log('debug', 'training.enhanced_objectives.registered', {
      count: objectives.length,
    });
  }

  execute(input: EvaluateEnhancedObjectivesInput): EvaluateEnhancedObjectivesOutput {
    const notifications: ObjectiveNotification[] = [];

    this.deps.evaluator.setNotificationCallback((notification) => {
      notifications.push(notification);
    });

    const context: ObjectiveContext = {
      currentStats: {
        budget: input.stats.budget,
        compliance: input.stats.compliance,
        threat: input.stats.threat,
        day: input.stats.day,
      },
      buildings: input.buildingCounts,
      products: input.stats.activeProducts,
      research: input.researchCompletedTechs,
      gameSpeed: input.gameSpeed,
      scenario: input.scenarioId,
    };

    this.deps.evaluator.evaluate(context);

    this.deps.logger?.log('debug', 'training.enhanced_objectives.evaluated', {
      notificationCount: notifications.length,
      day: input.stats.day,
    });

    return { notifications };
  }
}